// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl-next';
import { IconButton } from '@atlaskit/button/new';
import { ACTION, ACTION_SUBJECT, ACTION_SUBJECT_ID, EVENT_TYPE, INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { toolbarInsertBlockMessages } from '@atlaskit/editor-common/messages';
import Heading from '@atlaskit/heading';
import CloseIcon from '@atlaskit/icon/core/migration/close--cross';
import { Box, xcss } from '@atlaskit/primitives';
import { toggleInsertMenuRightRail } from '../../pm-plugins/commands';
import InsertMenu from '../ElementBrowser/InsertMenu';
import { useInsertMenuRailItems } from './useInsertMenuRailItems';
var panelWrapper = xcss({
  height: '100%'
});
var panelContentHeader = xcss({
  height: '32px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
});

/**
 * For insert menu in right rail experiment
 * - Clean up ticket ED-24801
 */
export var InsertMenuRail = function InsertMenuRail(_ref) {
  var editorView = _ref.editorView,
    options = _ref.options,
    api = _ref.api;
  var dropdownItems = useInsertMenuRailItems(editorView, options, api);
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var onInsert = function onInsert(_ref2) {
    var _api$core, _api$hyperlink, _api$imageUpload, _api$media, _api$mention, _api$emoji, _api$codeBlock, _api$blockType, _api$panel, _api$taskDecision, _api$taskDecision2, _api$rule, _api$core2, _api$quickInsert, _api$core3, _api$date, _api$placeholderText, _api$layout, _api$core4, _api$status;
    var item = _ref2.item;
    var state = editorView.state,
      dispatch = editorView.dispatch;
    var inputMethod = INPUT_METHOD.INSERT_MENU_RIGHT_RAIL;
    if (!api) {
      return;
    }
    if (!editorView.hasFocus()) {
      editorView.focus();
    }

    // Below is duplicated from ToolbarInsertBlock/index.tsx - this function is only called
    // for BlockMenuItem items, which are rendered in the insert menu when no search has been performed.
    // When a search is performed, the list will be filled by QuickInsertItems, which handle their own insertion.
    switch (item.value.name) {
      case 'link':
        // @ts-expect-error
        (_api$core = api.core) === null || _api$core === void 0 || _api$core.actions.execute(api === null || api === void 0 || (_api$hyperlink = api.hyperlink) === null || _api$hyperlink === void 0 ? void 0 : _api$hyperlink.commands.showLinkToolbar(inputMethod));
        break;
      case 'table':
        // workaround to solve race condition where cursor is not placed correctly inside table
        queueMicrotask(function () {
          var _api$table, _api$table$actions$in, _api$table$actions;
          // @ts-expect-error
          (_api$table = api.table) === null || _api$table === void 0 || (_api$table$actions$in = (_api$table$actions = _api$table.actions).insertTable) === null || _api$table$actions$in === void 0 || _api$table$actions$in.call(_api$table$actions, {
            action: ACTION.INSERTED,
            actionSubject: ACTION_SUBJECT.DOCUMENT,
            actionSubjectId: ACTION_SUBJECT_ID.TABLE,
            attributes: {
              inputMethod: inputMethod
            },
            eventType: EVENT_TYPE.TRACK
          })(state, dispatch);
        });
        break;
      case 'image upload':
        (_api$imageUpload = api.imageUpload) === null || _api$imageUpload === void 0 || _api$imageUpload.actions.startUpload()(state, dispatch);
        break;
      case 'media':
        var mediaState = (_api$media = api.media) === null || _api$media === void 0 ? void 0 : _api$media.sharedState.currentState();
        if (mediaState) {
          var _api$analytics;
          mediaState.showMediaPicker();
          // @ts-expect-error
          (_api$analytics = api.analytics) === null || _api$analytics === void 0 || _api$analytics.actions.attachAnalyticsEvent({
            action: ACTION.OPENED,
            actionSubject: ACTION_SUBJECT.PICKER,
            actionSubjectId: ACTION_SUBJECT_ID.PICKER_CLOUD,
            attributes: {
              inputMethod: inputMethod
            },
            eventType: EVENT_TYPE.UI
          });
        }
        break;
      case 'mention':
        var pluginState = (_api$mention = api.mention) === null || _api$mention === void 0 ? void 0 : _api$mention.sharedState.currentState();
        if (pluginState && pluginState.canInsertMention) {
          var _api$mention2;
          // @ts-expect-error
          (_api$mention2 = api.mention) === null || _api$mention2 === void 0 || (_api$mention2 = _api$mention2.actions) === null || _api$mention2 === void 0 || _api$mention2.openTypeAhead(inputMethod);
        }
        break;
      case 'emoji':
        // @ts-expect-error
        (_api$emoji = api.emoji) === null || _api$emoji === void 0 || _api$emoji.actions.openTypeAhead(inputMethod);
        break;
      case 'codeblock':
        (_api$codeBlock = api.codeBlock) === null || _api$codeBlock === void 0 || _api$codeBlock.actions.insertCodeBlock(inputMethod)(state, dispatch);
        break;
      case 'blockquote':
        // @ts-expect-error
        (_api$blockType = api.blockType) === null || _api$blockType === void 0 || _api$blockType.actions.insertBlockQuote(inputMethod)(state, dispatch);
        break;
      case 'panel':
        (_api$panel = api.panel) === null || _api$panel === void 0 || _api$panel.actions.insertPanel(inputMethod)(state, dispatch);
        break;
      case 'action':
        // @ts-expect-error
        (_api$taskDecision = api.taskDecision) === null || _api$taskDecision === void 0 || _api$taskDecision.actions.insertTaskDecision('taskList', inputMethod)(state, dispatch);
        break;
      case 'decision':
        // @ts-expect-error
        (_api$taskDecision2 = api.taskDecision) === null || _api$taskDecision2 === void 0 || _api$taskDecision2.actions.insertTaskDecision('decisionList', inputMethod)(state, dispatch);
        break;
      case 'horizontalrule':
        // @ts-expect-error
        (_api$rule = api.rule) === null || _api$rule === void 0 || _api$rule.actions.insertHorizontalRule(inputMethod)(state, dispatch);
        break;
      case 'macro':
        (_api$core2 = api.core) === null || _api$core2 === void 0 || _api$core2.actions.execute((_api$quickInsert = api.quickInsert) === null || _api$quickInsert === void 0 ? void 0 : _api$quickInsert.commands.openElementBrowserModal);
        break;
      case 'date':
        (_api$core3 = api.core) === null || _api$core3 === void 0 || _api$core3.actions.execute((_api$date = api.date) === null || _api$date === void 0 || (_api$date = _api$date.commands) === null || _api$date === void 0 ? void 0 : _api$date.insertDate({
          // @ts-expect-error
          inputMethod: inputMethod
        }));
        break;
      case 'placeholder text':
        (_api$placeholderText = api.placeholderText) === null || _api$placeholderText === void 0 || _api$placeholderText.actions.showPlaceholderFloatingToolbar(editorView.state, editorView.dispatch);
        break;
      case 'layout':
        // @ts-expect-error
        (_api$layout = api.layout) === null || _api$layout === void 0 || _api$layout.actions.insertLayoutColumns(inputMethod)(editorView.state, editorView.dispatch);
        break;
      case 'status':
        // @ts-expect-error
        (_api$core4 = api.core) === null || _api$core4 === void 0 || _api$core4.actions.execute((_api$status = api.status) === null || _api$status === void 0 || (_api$status = _api$status.commands) === null || _api$status === void 0 ? void 0 : _api$status.insertStatus(inputMethod));
        break;

      // https://product-fabric.atlassian.net/browse/ED-8053
      // @ts-ignore: OK to fallthrough to default
      case 'expand':
        if (options.allowExpand) {
          var _api$expand;
          (_api$expand = api.expand) === null || _api$expand === void 0 || _api$expand.actions.insertExpand(state, dispatch);
          break;
        }

      // eslint-disable-next-line no-fallthrough
      default:
      // leaving this blank for now
    }
  };
  useEffect(function () {
    if (!api) {
      return;
    }
    api.core.actions.execute(function (_ref3) {
      var _api$analytics2;
      var tr = _ref3.tr;
      (_api$analytics2 = api.analytics) === null || _api$analytics2 === void 0 || _api$analytics2.actions.attachAnalyticsEvent({
        action: ACTION.OPENED,
        // @ts-expect-error
        actionSubject: INPUT_METHOD.INSERT_MENU_RIGHT_RAIL,
        eventType: EVENT_TYPE.UI
      })(tr);
      return tr;
    });
    return function () {
      if (!api) {
        return;
      }
      api.core.actions.execute(function (_ref4) {
        var _api$analytics3;
        var tr = _ref4.tr;
        (_api$analytics3 = api.analytics) === null || _api$analytics3 === void 0 || _api$analytics3.actions.attachAnalyticsEvent({
          action: ACTION.CLOSED,
          // @ts-expect-error
          actionSubject: INPUT_METHOD.INSERT_MENU_RIGHT_RAIL,
          eventType: EVENT_TYPE.UI
        })(tr);
        return tr;
      });
    };
  }, [api]);
  return /*#__PURE__*/React.createElement(Box, {
    xcss: panelWrapper
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: panelContentHeader
  }, /*#__PURE__*/React.createElement(Heading, {
    size: "small",
    as: "h2"
  }, formatMessage(toolbarInsertBlockMessages.insertRightRailTitle)), /*#__PURE__*/React.createElement(IconButton, {
    appearance: "subtle",
    testId: "right-rail-insert-menu-close-button",
    label: formatMessage(toolbarInsertBlockMessages.closeInsertRightRail),
    icon: CloseIcon,
    onClick: function onClick() {
      if (!api) {
        return;
      }
      api.core.actions.execute(function (_ref5) {
        var _api$contextPanel;
        var tr = _ref5.tr;
        toggleInsertMenuRightRail(tr);
        (_api$contextPanel = api.contextPanel) === null || _api$contextPanel === void 0 || _api$contextPanel.actions.applyChange(tr);
        return tr;
      });
    }
  })), /*#__PURE__*/React.createElement(InsertMenu, {
    editorView: editorView,
    dropdownItems: dropdownItems,
    onInsert: onInsert,
    toggleVisiblity: function toggleVisiblity() {},
    showElementBrowserLink: true,
    pluginInjectionApi: api,
    isFullPageAppearance: true
  }));
};